import { useEffect, useState } from "react";
import { MessageSquareHeart, ExternalLink, BadgeCheck } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Stars } from "@/components/reviews/Stars";
import { ReviewDialog } from "@/components/reviews/ReviewDialog";

export const useReviews = () => {
  const [reviews, setReviews] = useState(null);
  const [googleUrl, setGoogleUrl] = useState("");
  useEffect(() => {
    api.get("/reviews").then(({ data }) => setReviews(data)).catch(() => setReviews([]));
    api.get("/reviews/settings").then(({ data }) => setGoogleUrl(data.google_review_url)).catch(() => {});
  }, []);
  return { reviews, googleUrl };
};

export const ReviewCard = ({ r }) => {
  const { date } = useLang();
  return (
    <div className="card-sacred p-6 h-full flex flex-col" data-testid={`review-card-${r.id.slice(0, 8)}`}>
      <Stars value={r.rating} />
      <p className="text-slate-300 leading-relaxed font-serif-soft text-lg mt-4 flex-1">“{r.comment}”</p>
      <div className="mt-5 flex items-center justify-between text-sm">
        <span className="text-[#F3DC8B] flex items-center gap-1.5">{r.name}{r.verified && <BadgeCheck size={15} className="text-[#D4AF37]" />}</span>
        <span className="text-slate-500 text-xs">{r.source === "google" ? <span className="px-2 py-0.5 rounded-full border border-slate-600 text-slate-300" data-testid="google-badge">Google</span> : date(r.created_at.slice(0, 10))}</span>
      </div>
    </div>
  );
};

export const ReviewActions = ({ googleUrl, onWrite }) => {
  const { t } = useLang();
  return (
    <div className="flex flex-wrap gap-4">
      <button onClick={onWrite} className="btn-gold px-7 py-3.5 flex items-center gap-2" data-testid="share-experience-button">
        <MessageSquareHeart size={18} /> {t("rv.share")}
      </button>
      {googleUrl && (
        <a href={googleUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost-gold px-6 py-3.5 flex items-center gap-2 text-sm" data-testid="google-review-link">
          {t("rv.google")} <ExternalLink size={15} />
        </a>
      )}
    </div>
  );
};

export const ReviewsSection = () => {
  const { t } = useLang();
  const { reviews, googleUrl } = useReviews();
  const [open, setOpen] = useState(false);
  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16" data-testid="home-reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <p className="eyebrow mb-3">{t("rv.eyebrow")}</p>
          <h2 className="text-3xl sm:text-4xl gold-text">{t("rv.heading")}</h2>
        </div>
        <ReviewActions googleUrl={googleUrl} onWrite={() => setOpen(true)} />
      </div>
      {reviews && reviews.length === 0 && <p className="text-slate-400" data-testid="reviews-empty">{t("rv.empty")}</p>}
      {reviews && reviews.length > 0 && (
        <Carousel opts={{ align: "start", loop: reviews.length > 1 }} className="px-10 sm:px-14" data-testid="reviews-carousel">
          <CarouselContent>
            {reviews.map((r) => (
              <CarouselItem key={r.id} className="md:basis-1/2 lg:basis-1/3"><ReviewCard r={r} /></CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-0 border-[#D4AF37]/50 bg-[#0F172A] text-[#F3DC8B] hover:bg-[#D4AF37] hover:text-[#4a0505]" data-testid="reviews-prev" />
          <CarouselNext className="right-0 h-10 w-10 border-[#D4AF37] bg-[#D4AF37] text-[#4a0505] hover:bg-[#E8C55A]" data-testid="reviews-next" />
        </Carousel>
      )}
      <ReviewDialog open={open} onOpenChange={setOpen} />
    </section>
  );
};
